<template>
  <div style="padding-top: 3em;">
    <h4 class="title">Mayor &amp; Elected Member Attendance Event and Speech Form</h4>
    <p>Attendance of the Mayor or Elected Members will not be confirmed without completion and return of this form.</p>
    <br />

    <div v-if="message" class="notification is-primary">{{ message }}</div>

    <form-wizard @submit.prevent="onSubmit" @on-complete="onSubmit" shape="tab" color="#17C4EE" title subtitle
      ref="wizard" v-if="!message">
      <input type="hidden" name="_token" :value="csrf" />
      <input type="hidden" v-model="form" />

      <div style="padding: 1em 0px; color: #aaa;">
        <small>* Required Fields</small>
      </div>

      <!-- ************************* -->
      <!-- TAB #1 - EVENT DETAILS -->
      <!-- ************************* -->
      <tab-content title="Event Details" :before-change="validateStep1">
        <div class="columns">
          <div class="column is-6">
            <div class="field">
              <label class="label">Event Name *</label>
              <div class="control">
                <input type="text" autofocus id="eventName" name="eventName" v-model="eventName"
                  :status="$v.eventName.$error ? 'error' : null" placeholder="Please enter the event name"
                  :class="{ 'input': true, 'is-danger': $v.eventName.$error }" @blur="$v.eventName.$touch()" />
                <span v-if="$v.eventName.$error && !$v.eventName.required" class="help is-danger">Event name is
                  required</span>
                <span v-if="$v.eventName.$error && !$v.eventName.AlphNumCharsValidator" class="help is-danger">Invalid
                  characters entered</span>
              </div>
            </div>
          </div>
        </div>
        <div class="columns">
          <div class="column is-6">
            <div class="field">
              <label class="label">Event Location *</label>
              <div class="control">
                <input type="text" id="eventLocation" name="eventLocation" v-model="eventLocation"
                  :status="$v.eventLocation.$error ? 'error' : null" placeholder="Please enter the event location"
                  :class="{ 'input': true, 'is-danger': $v.eventLocation.$error }" @blur="$v.eventLocation.$touch()" />
                <span v-if="$v.eventLocation.$error && !$v.eventLocation.required" class="help is-danger">Event location
                  is required</span>
                <span v-if="$v.eventLocation.$error && !$v.eventLocation.AlphNumCharsValidator"
                  class="help is-danger">Invalid characters entered</span>
              </div>
            </div>
          </div>
        </div>
        <div class="columns is-multiline">
          <div class="column is-8">
            <div class="field">
              <label class="label">Event Date/Time *</label>
              <div class="control">
                <input type="text" :class="'input ' + classEventDate" style="width: 7em; float: left; display: inline;"
                  :value="customFormatter(eventDatePicker)" readonly />
                <datepicker :format="customFormatter" id="datepicker" :disabled-dates="disabled" :input-class="'input'"
                  @input="checkEventDateValue" name="eventDatePicker" :placeholder="'dd/mm/yyyy'"
                  v-model="eventDatePicker"></datepicker>
                <input type="text" placeholder="hh:mm" :class="{ 'input': true, 'is-danger': $v.eventTime.$error }"
                  style="width: 7em; float: left; display: inline;" @input="$v.eventTime.$touch()" id="eventTime"
                  name="eventTime" v-model="eventTime" />
                <span class="select" style="vertical-align: middle;">
                  <select v-model="eventTimeAMPM">
                    <option>AM</option>
                    <option>PM</option>
                  </select>
                </span>
                <span v-if="$v.eventTime.$error && !$v.eventTime.required" class="help is-danger"
                  style="display: inline;">Time is required</span>
              </div>
              <div style="margin: .6em 0; font-size: .95em;">
                {{ formatDateTime(eventDatePicker, eventTime + ' ' + eventTimeAMPM) }}</div>
            </div>
          </div>
        </div>

        <div class="columns">
          <div class="column is-6">
            <div class="field">
              <label class="label">Organiser Name *</label>
              <div class="control">
                <input type="text" id="organiserName" name="organiserName" v-model="organiserName"
                  :status="$v.organiserName.$error ? 'error' : null" placeholder="Please enter the organiser name"
                  :class="{ 'input': true, 'is-danger': $v.organiserName.$error }" @blur="$v.organiserName.$touch()" />
                <span v-if="$v.organiserName.$error && !$v.organiserName.required" class="help is-danger">Organiser name
                  is required</span>
                <span v-if="$v.organiserName.$error && !$v.organiserName.AlphNumCharsValidator"
                  class="help is-danger">Invalid characters entered</span>
              </div>
            </div>
          </div>
        </div>

        <div class="columns">
          <div class="column is-6">
            <div class="field">
              <label class="label">Organiser Contact *</label>
              <div class="control">
                <input type="text" id="organiserContact" name="organiserContact" v-model="organiserContact"
                  :status="$v.organiserContact.$error ? 'error' : null" placeholder="Please enter the organiser contact"
                  :class="{ 'input': true, 'is-danger': $v.organiserContact.$error }"
                  @blur="$v.organiserContact.$touch()" />
                <span v-if="$v.organiserContact.$error && !$v.organiserContact.required"
                  class="help is-danger">Organiser contact is required</span>
                <span v-if="$v.organiserContact.$error && !$v.organiserContact.AlphNumCharsValidator"
                  class="help is-danger">Invalid characters entered</span>
              </div>
            </div>
          </div>
        </div>

        <div class="columns">
          <div class="column is-6">
            <div class="field">
              <label class="label">Organiser Email *</label>
              <div class="control">
                <input type="text" id="emailAddress" name="emailAddress" v-model="emailAddress"
                  :status="$v.emailAddress.$error ? 'error' : null" placeholder="Please enter the organiser email"
                  :class="{ 'input': true, 'is-danger': $v.emailAddress.$error }" @blur="$v.emailAddress.$touch()" />
                <span v-if="$v.emailAddress.$error && !$v.emailAddress.required" class="help is-danger">Organiser
                  email is required</span>
                <span v-if="$v.emailAddress.$error && !$v.emailAddress.email" class="help is-danger">Must be an email
                  address</span>
              </div>
            </div>
          </div>
        </div>
      </tab-content>

      <!-- ************************* -->
      <!-- TAB #2 - ADDITIONAL DETAILS -->
      <!-- ************************* -->
      <tab-content title="Additional Details" :before-change="validateStep2">
        <div class="columns">
          <div class="column is-6">
            <div class="field">
              <label class="label">Purpose of Event *</label>
              <div class="control">
                <textarea class="textarea" id="eventPurpose" name="eventPurpose" v-model="eventPurpose"
                  :status="$v.eventPurpose.$error ? 'error' : null"
                  placeholder="What are the intended outcomes of this event?"
                  :class="{ 'input': true, 'is-danger': $v.eventPurpose.$error }"
                  @blur="$v.eventPurpose.$touch()"></textarea>
                <span v-if="$v.eventPurpose.$error && !$v.eventPurpose.required" class="help is-danger">Purpose of event
                  is required</span>
                <span v-if="$v.eventPurpose.$error && !$v.eventPurpose.AlphNumCharsValidator"
                  class="help is-danger">Invalid characters entered</span>
              </div>
            </div>
          </div>
        </div>

        <div class="columns">
          <div class="column is-6">
            <div class="field">
              <label class="label">Has a car park been reserved? *</label>
              <div class="control">
                <div class="select">
                  <select id="eventCarkParkReserved" name="eventDeventCarkParkReservedessCode"
                    v-model="eventCarkParkReserved" :status="$v.eventCarkParkReserved.$error ? 'error' : null"
                    @blur="$v.eventCarkParkReserved.$touch()">
                    <option value>--Select an option--</option>
                    <option>Yes</option>
                    <option>No</option>
                  </select>
                  <span v-if="$v.eventCarkParkReserved.$error && !$v.eventCarkParkReserved.required"
                    class="help is-danger">This is required</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="columns" v-if="eventCarkParkReserved=='Yes'">
          <div class="column is-6">
            <div class="field">
              <label class="label">Where is the car park located? *</label>
              <div class="control">
                <textarea class="textarea" id="eventCarkParkReservedLocation" name="eventCarkParkReservedLocation"
                  v-model="eventCarkParkReservedLocation"
                  :status="$v.eventCarkParkReservedLocation.$error ? 'error' : null"
                  placeholder="Please provide car park details"
                  :class="{ 'input': true, 'is-danger': $v.eventCarkParkReservedLocation.$error }"
                  @blur="$v.eventCarkParkReservedLocation.$touch()"></textarea>
                <span v-if="$v.eventCarkParkReservedLocation.$error && !$v.eventCarkParkReservedLocation.required"
                  class="help is-danger">This is required</span>
                <span
                  v-if="$v.eventCarkParkReservedLocation.$error && !$v.eventCarkParkReservedLocation.AlphNumCharsValidator"
                  class="help is-danger">Invalid characters entered</span>
              </div>
            </div>
          </div>
        </div>

        <div class="columns">
          <div class="column is-6">
            <div class="field">
              <label class="label">Dress Code *</label>
              <div class="control">
                <div class="select">
                  <select id="eventDressCode" name="eventDressCode" v-model="eventDressCode"
                    :status="$v.eventDressCode.$error ? 'error' : null" @blur="$v.eventDressCode.$touch()">
                    <option value>--Select an option--</option>
                    <option>Casual</option>
                    <option>Formal</option>
                    <option>Black Tie</option>
                    <option>Mayor Robes</option>
                  </select>
                  <span v-if="$v.eventDressCode.$error && !$v.eventDressCode.required" class="help is-danger">Dress code
                    is required</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="columns">
          <div class="column is-6">
            <div class="field">
              <label class="label">Is a Partner Invited *</label>
              <div class="control">
                <div class="select">
                  <select id="eventPartnerInvited" name="eventPartnerInvited" v-model="eventPartnerInvited"
                    :status="$v.eventPartnerInvited.$error ? 'error' : null" @blur="$v.eventPartnerInvited.$touch()">
                    <option value>--Select--</option>
                    <option>Yes</option>
                    <option>No</option>
                  </select>
                  <span v-if="$v.eventPartnerInvited.$error && !$v.eventPartnerInvited.required"
                    class="help is-danger">This field is required</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="columns">
          <div class="column is-6">
            <div class="field">
              <label class="label">Invited Elected Members *</label>
              <div class="select">
                <select id="eventInvitedElectedMembers" name="eventInvitedElectedMembers"
                  v-model="eventInvitedElectedMembers" :status="$v.eventInvitedElectedMembers.$error ? 'error' : null"
                  @blur="$v.eventInvitedElectedMembers.$touch()">
                  <option value>--Select--</option>
                  <option>Yes</option>
                  <option>No</option>
                </select>
                <span v-if="$v.eventInvitedElectedMembers.$error && !$v.eventInvitedElectedMembers.required"
                  class="help is-danger">This field is required</span>
              </div>
            </div>
          </div>
        </div>
        <div class="columns" v-if="eventInvitedElectedMembers=='Yes'">
          <div class="column is-6">
            <div class="field">
              <label class="label">Elected Member Details? *</label>
              <div class="control">
                <textarea class="textarea" id="eventInvitedElectedMembersDetails"
                  name="eventInvitedElectedMembersDetails" v-model="eventInvitedElectedMembersDetails"
                  :status="$v.eventInvitedElectedMembersDetails.$error ? 'error' : null"
                  placeholder="Please provide names"
                  :class="{ 'input': true, 'is-danger': $v.eventInvitedElectedMembersDetails.$error }"
                  @blur="$v.eventInvitedElectedMembersDetails.$touch()"></textarea>
                <span
                  v-if="$v.eventInvitedElectedMembersDetails.$error && !$v.eventInvitedElectedMembersDetails.required"
                  class="help is-danger">This field is required</span>
                <span
                  v-if="$v.eventInvitedElectedMembersDetails.$error && !$v.eventInvitedElectedMembersDetails.AlphNumCharsValidator"
                  class="help is-danger">Invalid characters entered</span>
              </div>
            </div>
          </div>
        </div>

        <div class="columns">
          <div class="column is-6">
            <div class="field">
              <label class="label">Invited VIPs *</label>
              <div class="select">
                <select id="eventInvitedVIPs" name="eventInvitedVIPs" v-model="eventInvitedVIPs"
                  :status="$v.eventInvitedVIPs.$error ? 'error' : null" @blur="$v.eventInvitedVIPs.$touch()">
                  <option value>--Select--</option>
                  <option>Yes</option>
                  <option>No</option>
                </select>
                <span v-if="$v.eventInvitedVIPs.$error && !$v.eventInvitedVIPs.required" class="help is-danger">This
                  field is required</span>
              </div>
            </div>
          </div>
        </div>
        <div class="columns" v-if="eventInvitedVIPs=='Yes'">
          <div class="column is-6">
            <div class="field">
              <label class="label">VIP Details? *</label>
              <div class="control">
                <textarea class="textarea" id="eventInvitedVIPsDetails" name="eventInvitedVIPsDetails"
                  v-model="eventInvitedVIPsDetails" :status="$v.eventInvitedVIPsDetails.$error ? 'error' : null"
                  placeholder="Please provide names"
                  :class="{ 'input': true, 'is-danger': $v.eventInvitedVIPsDetails.$error }"
                  @blur="$v.eventInvitedVIPsDetails.$touch()"></textarea>
                <span v-if="$v.eventInvitedVIPsDetails.$error && !$v.eventInvitedVIPsDetails.required"
                  class="help is-danger">This field is required</span>
                <span v-if="$v.eventInvitedVIPsDetails.$error && !$v.eventInvitedVIPsDetails.AlphNumCharsValidator"
                  class="help is-danger">Invalid characters entered</span>
              </div>
            </div>
          </div>
        </div>


        <div class="columns">
          <div class="column is-6">
            <div class="field">
              <label class="label">Invited Media *</label>
              <div class="select">
                <select id="eventInvitedMedia" name="eventInvitedMedia" v-model="eventInvitedMedia"
                  :status="$v.eventInvitedMedia.$error ? 'error' : null" @blur="$v.eventInvitedMedia.$touch()">
                  <option value>--Select--</option>
                  <option>Yes</option>
                  <option>No</option>
                </select>
                <span v-if="$v.eventInvitedMedia.$error && !$v.eventInvitedMedia.required" class="help is-danger">This
                  field is required</span>
              </div>
            </div>
          </div>
        </div>
        <div class="columns" v-if="eventInvitedMedia=='Yes'">
          <div class="column is-6">
            <div class="field">
              <label class="label">Media Details? *</label>
              <div class="control">
                <textarea class="textarea" id="eventInvitedMediaDetails" name="eventInvitedMediaDetails"
                  v-model="eventInvitedMediaDetails" :status="$v.eventInvitedMediaDetails.$error ? 'error' : null"
                  placeholder="Please provide names"
                  :class="{ 'input': true, 'is-danger': $v.eventInvitedMediaDetails.$error }"
                  @blur="$v.eventInvitedMediaDetails.$touch()"></textarea>
                <span v-if="$v.eventInvitedMediaDetails.$error && !$v.eventInvitedMediaDetails.required"
                  class="help is-danger">This field is required</span>
                <span v-if="$v.eventInvitedMediaDetails.$error && !$v.eventInvitedMediaDetails.AlphNumCharsValidator"
                  class="help is-danger">Invalid characters entered</span>
              </div>
            </div>
          </div>
        </div>


        <div class="columns">
          <div class="column is-6">
            <div class="field">
              <label class="label">Has Council Contributed to the Event *</label>
              <div class="select">
                <select id="councilFunding" name="councilFunding" v-model="councilFunding"
                  :status="$v.councilFunding.$error ? 'error' : null" @blur="$v.councilFunding.$touch()">
                  <option value>--Select--</option>
                  <option>Yes</option>
                  <option>No</option>
                </select>
                <span v-if="$v.councilFunding.$error && !$v.councilFunding.required" class="help is-danger">This field
                  is required</span>
              </div>
            </div>
          </div>
        </div>
        <div class="columns" v-if="councilFunding=='Yes'">
          <div class="column is-6">
            <div class="field">
              <label class="label">How much of the total amount has Council contributed? *</label>
              <div class="control">
                <input type="text" id="councilFundingDetails" name="councilFundingDetails"
                  v-model="councilFundingDetails" :status="$v.councilFundingDetails.$error ? 'error' : null"
                  placeholder="Please enter the amount"
                  :class="{ 'input': true, 'is-danger': $v.councilFundingDetails.$error }"
                  @blur="$v.councilFundingDetails.$touch()" />
                <span v-if="$v.councilFundingDetails.$error && !$v.councilFundingDetails.required"
                  class="help is-danger">This field is required</span>
                <span v-if="$v.councilFundingDetails.$error && !$v.councilFundingDetails.AlphNumCharsValidator"
                  class="help is-danger">Invalid characters entered</span>
              </div>
            </div>
          </div>
        </div>

        <div class="columns">
          <div class="column is-6">
            <div class="field">
              <label class="label">Will Food be Served at the Event? *</label>
              <div class="select">
                <select id="catering" name="catering" v-model="catering" :status="$v.catering.$error ? 'error' : null"
                  @blur="$v.catering.$touch()">
                  <option value>--Select--</option>
                  <option>No Food</option>
                  <option>Refreshments</option>
                  <option>Finger Food</option>
                  <option>Full Meal</option>
                </select>
                <span v-if="$v.catering.$error && !$v.catering.required" class="help is-danger">This field
                  is required</span>
              </div>
            </div>
          </div>
        </div>


      </tab-content>

      <!-- ************************* -->
      <!-- TAB #3 - MAYOR SPEECH DETAILS -->
      <!-- ************************* -->
      <tab-content title="Mayor Speech Details" :before-change="validateStep3">

        <div class="columns">
          <div class="column is-6">
            <div class="field">
              <label class="label">Is a speech required? *</label>
              <div class="select">
                <select id="speechRequired" name="speechRequired" v-model="speechRequired"
                  :status="$v.speechRequired.$error ? 'error' : null" @blur="$v.speechRequired.$touch()">
                  <option value>--Select--</option>
                  <option>Yes</option>
                  <option>No</option>
                </select>
                <span v-if="$v.speechRequired.$error && !$v.speechRequired.required" class="help is-danger">This field
                  is required</span>
              </div>
            </div>
          </div>
        </div>
        <template v-if="speechRequired=='Yes'">
          <div class="columns">
            <div class="column is-6">
              <div class="field">
                <label class="label">Speech Time *</label>
                <div class="control">
                  <input type="text" id="speechTime" name="speechTime" v-model="speechTime"
                    :status="$v.speechTime.$error ? 'error' : null" placeholder="What time is the speech scheduled for"
                    :class="{ 'input': true, 'is-danger': $v.speechTime.$error }" @blur="$v.speechTime.$touch()" />
                  <span v-if="$v.speechTime.$error && !$v.speechTime.required" class="help is-danger">Speech time is
                    required</span>
                  <span v-if="$v.speechTime.$error && !$v.speechTime.AlphNumCharsValidator"
                    class="help is-danger">Invalid
                    characters entered</span>
                </div>
              </div>
            </div>
          </div>

          <div class="columns">
            <div class="column is-6">
              <div class="field">
                <label class="label">Speech Duration *</label>
                <div class="control">
                  <input type="text" id="speechDuration" name="speechDuration" v-model="speechDuration"
                    :status="$v.speechDuration.$error ? 'error' : null"
                    placeholder="What time is the speech scheduled for"
                    :class="{ 'input': true, 'is-danger': $v.speechDuration.$error }"
                    @blur="$v.speechDuration.$touch()" />
                  <span v-if="$v.speechDuration.$error && !$v.speechDuration.required" class="help is-danger">Speech
                    Duration is
                    required</span>
                  <span v-if="$v.speechDuration.$error && !$v.speechDuration.AlphNumCharsValidator"
                    class="help is-danger">Invalid
                    characters entered</span>
                </div>
              </div>
            </div>
          </div>

          <div class="columns">
            <div class="column is-6">
              <div class="field">
                <label class="label">Speech Topics *</label>
                <div class="control">
                  <textarea class="textarea" id="speechTopics" name="speechTopics" v-model="speechTopics"
                    :status="$v.speechTopics.$error ? 'error' : null"
                    placeholder="What are the 3 most important things that should be included in the speech?"
                    :class="{ 'input': true, 'is-danger': $v.speechTopics.$error }"
                    @blur="$v.speechTopics.$touch()"></textarea>
                  <span v-if="$v.speechTopics.$error && !$v.speechTopics.required" class="help is-danger">This field is
                    required</span>
                  <span v-if="$v.speechTopics.$error && !$v.speechTopics.AlphNumCharsValidator"
                    class="help is-danger">Invalid characters entered</span>
                </div>
              </div>
            </div>
          </div>

          <div class="columns">
            <div class="column is-6">
              <div class="field">
                <label class="label">Background Information *</label>
                <div class="control">
                  <textarea class="textarea" id="speechBackgroundInformation" name="speechBackgroundInformation"
                    v-model="speechBackgroundInformation"
                    :status="$v.speechBackgroundInformation.$error ? 'error' : null"
                    placeholder="Imagine we know nothing about your event. Please tell us as much as possible to assist with writing a speech."
                    :class="{ 'input': true, 'is-danger': $v.speechBackgroundInformation.$error }"
                    @blur="$v.speechBackgroundInformation.$touch()"></textarea>
                  <span v-if="$v.speechBackgroundInformation.$error && !$v.speechBackgroundInformation.required"
                    class="help is-danger">This field is
                    required</span>
                  <span
                    v-if="$v.speechBackgroundInformation.$error && !$v.speechBackgroundInformation.AlphNumCharsValidator"
                    class="help is-danger">Invalid characters entered</span>
                </div>
              </div>
            </div>
          </div>
        </template>
      </tab-content>
      <br />
    </form-wizard>
  </div>
</template>

<script>
  const isDate = value => moment(value, "DD/MM/YYYY", true).isValid();

import VueRecaptcha from "vue-recaptcha";

import PropertyAddress from "../../../../../../resources/assets/js/components/address/Typeahead-CouncilAddress.vue";
import GoogleAddressAutocomplete from "../../../../../../resources/assets/js/components/address/GoogleAddressAutocomplete.vue";
import FileDragDropInstant from "../../../../../../Modules/FileManagement/Resources/assets/js/components/FileDragDropInstant.vue";

import {
  alpha,
  required,
  requiredIf,
  email,
  numeric,
  alphaNum,
  minValue
} from "vuelidate/lib/validators";
import { validationMixin } from "vuelidate";
import moment from "moment";

// Custom Validators
import { TimeValidator } from "../../../../../../resources/assets/js/validators/time-validator";
import { AlphNumCharsValidator } from "../../../../../../resources/assets/js/validators/alpha-num-chars-validator";

import { FormWizard, TabContent } from "vue-form-wizard";
import "vue-form-wizard/dist/vue-form-wizard.min.css";

import datepicker from "vuejs-datepicker";

export default {
  components: {
    PropertyAddress,
    GoogleAddressAutocomplete,
    FileDragDropInstant,
    VueRecaptcha,
    FormWizard,
    TabContent,
    datepicker
  },

  props: {
    form: {}
  },

  data() {
    return {
      googleCaptchaKey: "6LcyrE0UAAAAAExxoj3NBm1vA-WMu88CIigyzkBU",
      enableGoogleCaptch: false,

      csrf: "",
      formTitle: this.form,
      message: "",
      submitted: false,
      loading: false,

      fileIDs: [],

      // ***********
      // FORM DATA
      // ***********

      format: "dd/MM/yyyy",
      disabled: {
        to: new Date(
          new Date().getFullYear(),
          new Date().getMonth(),
          new Date().getDate() + 1
        )
      },
      // -- Event Details
      eventName: "",
      eventLocation: "",
      eventDate: "",
      eventDatePicker: new Date(moment().add(1, "day")),
      eventTime: "00:00",
      eventTimeAMPM: "AM",
      classEventDate: "",
      organiserName: "",
      organiserContact: "",
      emailAddress: "",

      // -- Additional Details
      eventPurpose: "",
      eventDressCode: "",
      eventPartnerInvited: "",
      eventCarkParkReserved: "",
      eventCarkParkReservedLocation: "",
      eventInvitedVIPs: "",
      eventInvitedVIPsDetails: "",
      eventInvitedElectedMembers: "",
      eventInvitedElectedMembersDetails: "",
      eventInvitedMedia: "",
      eventInvitedMediaDetails: "",
      councilFunding: "",
      councilFundingDetails: "",
      catering: "",

      // -- Mayor Speech Details
      speechRequired: "",
      speechTime: "",
      speechDuration: "",
      speechTopics: "",
      speechBackgroundInformation: "",

      agree: false
    };
  },

  validations: {
    eventName: { required, AlphNumCharsValidator },
    eventLocation: { required, AlphNumCharsValidator },
    eventDate: {},
    eventTime: {},
    organiserName: { required, AlphNumCharsValidator },
    organiserContact: { required },
    emailAddress: { required, email },

    formStep1: [
      "eventName",
      "eventLocation",
      "organiserName",
      "organiserContact",
      "emailAddress"
    ],

    eventPurpose: { required, AlphNumCharsValidator },
    eventDressCode: { required },
    eventPartnerInvited: { required },
    eventCarkParkReserved: { required },
    eventCarkParkReservedLocation: {
      required: requiredIf(function() {
        return this.eventCarkParkReserved == "Yes";
      })
    },
    eventInvitedVIPs: { required },
    eventInvitedVIPsDetails: {
      required: requiredIf(function() {
        return this.eventInvitedVIPs == "Yes";
      })
    },
    eventInvitedElectedMembers: { required },
    eventInvitedElectedMembersDetails: {
      required: requiredIf(function() {
        return this.eventInvitedElectedMembers == "Yes";
      })
    },
    eventInvitedMedia: { required },
    eventInvitedMediaDetails: {
      required: requiredIf(function() {
        return this.eventInvitedMedia == "Yes";
      })
    },
    councilFunding: { required },
    councilFundingDetails: {
      required: requiredIf(function() {
        return this.councilFunding == "Yes";
      })
    },
    catering: { required },

    formStep2: [
      "eventPurpose",
      "eventCarkParkReserved",
      "eventCarkParkReservedLocation",
      "eventDressCode",
      "eventPartnerInvited",
      "eventInvitedElectedMembers",
      "eventInvitedElectedMembersDetails",
      "eventInvitedVIPs",
      "eventInvitedVIPsDetails",

      "eventInvitedMedia",
      "eventInvitedMediaDetails",

      "councilFunding",
      "councilFundingDetails",
      "catering"
    ],

    speechRequired: { required },
    speechTime: { 
      required: requiredIf(function() {
        return this.speechRequired == "Yes";
      })
    },
    speechDuration: { 
      required: requiredIf(function() {
        return this.speechRequired == "Yes";
      })
    },
    speechTopics: { 
      required: requiredIf(function() {
        return this.speechRequired == "Yes";
      })
    },
    speechBackgroundInformation: { 
      required: requiredIf(function() {
        return this.speechRequired == "Yes";
      })
    },

    formStep3: [
      "speechRequired",
      "speechTime",
      "speechDuration",
      "speechTopics",
      "speechBackgroundInformation"
    ]
  },

  watch: {
    message: function(val) {
      if (!val === "") {
        this.loading = false;
      }
    },
    eventDatePicker: function() {
      this.eventDate = this.saveDateTime(
        this.eventDatePicker,
        this.eventTime + " " + this.eventTimeAMPM
      );
    },
    eventTime: function() {
      this.eventDate = this.saveDateTime(
        this.eventDatePicker,
        this.eventTime + " " + this.eventTimeAMPM
      );
      this.updateAMPM();
    },
    eventTimeAMPM: function() {
      this.eventDate = this.saveDateTime(
        this.ieventDatePicker,
        this.eventTime + " " + this.eventTimeAMPM
      );
      this.updateAMPM();
    }
  },

  computed: {
    fullName: function() {
      return this.organiserName;
    },
    precis: function() {
      return "Event and Speech Form - " + this.eventName;
    },
    formData: function() {
      var fields = {};

      fields.emailAddress = this.emailAddress;
      fields.eventName = this.eventName;
      fields.eventLocation = this.eventLocation;
      fields.eventDate = this.eventDate;
      fields.eventTime = this.eventTime;
      fields.eventTimeAMPM = this.eventTimeAMPM;
      fields.organiserName = this.organiserName;
      fields.organiserContact = this.organiserContact;
      fields.eventPurpose = this.eventPurpose;
      fields.eventDressCode = this.eventDressCode;
      fields.eventPartnerInvited = this.eventPartnerInvited;
      fields.eventCarkParkReserved = this.eventCarkParkReserved;
      fields.eventCarkParkReservedLocation = this.eventCarkParkReservedLocation;
      fields.eventInvitedVIPs = this.eventInvitedVIPs;
      fields.eventInvitedVIPsDetails = this.eventInvitedVIPsDetails;
      fields.eventInvitedElectedMembers = this.eventInvitedElectedMembers;
      fields.eventInvitedElectedMembersDetails = this.eventInvitedElectedMembersDetails;
      fields.eventInvitedMedia = this.eventInvitedMedia;
      fields.eventInvitedMediaDetails = this.eventInvitedMediaDetails;
      fields.councilFunding = this.councilFunding;
      fields.councilFundingDetails = this.councilFundingDetails;
      fields.catering = this.catering;
      fields.speechRequired = this.speechRequired;
      fields.speechTime = this.speechTime;
      fields.speechDuration = this.speechDuration;
      fields.speechTopics = this.speechTopics;
      fields.speechBackgroundInformation = this.speechBackgroundInformation;

      fields.precis = this.precis;
      var subjects = [];
      subjects.push("2.38.3.2");
      if(this.speechRequired) {
        subjects.push("2.38.1.2");
      }
      fields.subject = subjects;

      var actions = [];
      actions.push("A2.38.3.2");
      fields.action = actions;

      return JSON.stringify(fields);
    }
  },

  methods: {
    process(fieldName, file, metadata, load, error, progress, abort) {
      console.log("process start");
      console.log(file);
      console.log(metadata);
      console.log(fieldName);
      var self = this;
      try {
        progress(true, 0, 1);

        axios
          .post("/api/filemanagement/process", {
            _token: this.csrf,
            metadata
          })
          .then(response => {
            console.log("promise response: " + response.data.message);
          });

        return {
          abort: () => {
            abort();
            // uploadTask.cancel();
          }
        };
      } catch (e) {
        this.handleError(error, e);
        return {
          abort: () => {
            abort();
          }
        };
      }
    },

    processFile(error, file) {
      // process db.
      console.log("processed " + file.filename);
    },

    handleFilePondInit: function() {},

    // FILE UPLOADS
    setFilenames(value) {
      //alert(value)
      this.files = value1;
      this.filesUploaded = true;
    },
    setDescriptions(value) {
      //alert(value)
      this.descriptions = value;
    },

    // DATE PICKER
    customFormatter(date) {
      return moment(date).format("DD/MM/YYYY");
    },
    saveDate: function(date) {
      return moment(date, "DD/MM/YYYY").format("YYYY/MM/DD");
    },
    saveDateTime: function(date, time) {
      let datetime = moment(date).format("DD/MM/YYYY") + " " + time;
      return moment(datetime, "DD/MM/YYYY hh:mm a").format(
        "YYYY/MM/DD hh:mm a"
      );
    },
    formatDate: function(date) {
      return moment(date, "DD/MM/YYYY").format("LLLL");
    },
    formatDateTime: function(date, time) {
      let datetime = moment(date).format("DD/MM/YYYY") + " " + time;
      return moment(datetime, "DD/MM/YYYY hh:mm a").format("LLLL");
    },
    checkEventDateError: function() {
      if (this.$v.eventDate.$error) {
        this.classEventDate = "is-danger";
      }
    },
    checkEventDateValue: function() {
      this.$v.eventDate.$touch();
      this.checkEventDateError();
    },
    updateAMPM: function() {
      let arrTime = this.eventTime.split(":");

      if (parseInt(arrTime[0]) > 12 && parseInt(arrTime[0]) < 24) {
        this.eventTimeAMPM = "PM";
      } else if (parseInt(arrTime[0]) > 23) {
        let mins = "";
        if (arrTime[1] >= 0) {
          mins = arrTime[1];
        } else {
          mins = "00";
        }
        this.eventTime = "00:" + mins;
        this.eventTimeAMPM = "AM";
      } else {
        //this.incidentTimeAMPM = 'AM'
      }
    },

    checkPersonalDOBError: function() {
      if (this.$v.personalDOB.$error) {
        this.classPersonalDOB = "is-danger";
      }
    },
    checkPersonalDOBValue: function() {
      this.$v.personalDOB.$touch();
      this.checkPersonalDOBError();
    },

    // STEP VALIDATION
    validateStep1() {
      this.$v.formStep1.$touch();
      return !this.$v.formStep1.$invalid;
    },
    validateStep2() {
      this.$v.formStep2.$touch();
      return !this.$v.formStep2.$invalid;
    },
    validateStep3() {
      this.$v.formStep3.$touch();
      return !this.$v.formStep3.$invalid;
    },

    // FILE ATTACHMENT HANDLING
    saveFileIDs(value) {
      this.fileIDs = value;
    },
    appendFileIDs(value) {
      let self = this;
      value.forEach(function(v) {
        if (self.fileIDs.indexOf(v) < 0) {
          self.fileIDs.push(v);
        }
      });
    },
    removeFileID(value) {
      this.fileIDs.splice(this.fileIDs.indexOf(value), 1);
    },

    saveIncidentAddress(value) {
      this.incidentAddress = value;
    },
    saveIncidentPropertyNo(value) {
      this.incidentPropertyNo = value;
    },

    saveClaimantAddress(value) {
      this.claimantAddress = value;
      if (this.sameAddress == true) {
        this.incidentAddress = this.claimantAddress;
      }
    },
    saveClaimantAddressTyping(value) {
      this.claimantAddressTyping = value;
      if (this.sameAddress == true) {
        this.incidentAddress = this.claimantAddressTyping;
      }
    },
    saveClaimantPropertyNo(value) {
      this.claimantPropertyNo = value;

      if (this.sameAddress == true) {
        this.incidentPropertyNo = this.claimantPropertyNo;
      }
    },

    clearClaimantAddress() {
      this.claimantAddress = "";
      this.claimantAddressTyping = "";
      this.claimantPropertyNo = "";
    },
    clearIncidentAddress() {
      this.incidentAddress = "";
      this.incidentPropertyNo = "";
    },

    onSubmit() {
      if (this.validateStep3()) {
        if (this.enableGoogleCaptch) {
          this.$refs.invisibleRecaptcha.execute();
        } else {
          this.submitForm();
        }
      }
    },

    submitForm() {
      this.loading = true;
      axios
        .post("/onlineform/" + this.form, {
          _token: this.csrf,
          formTitle: this.formTitle,
          fullName: this.fullName,
          emailAddress: this.emailaddress,
          contactNumber: this.organiserContact,
          precis: this.precis,
          formData: this.formData,
          message: ""
        })
        .then(response => {
          console.log(response.data.message);
          this.message = response.data.message;
          this.clear();

          setTimeout(() => (this.submitted = true), 5000);
          this.loading = false;
        });
    },

    onVerify: function(response) {
      if (response) {
        axios
          .post("/api/recaptchaSiteVerify", {
            value: response
          })
          .then(gresponse => {
            console.log(gresponse);

            if (gresponse["data"] === "valid") {
              this.submitForm();
            } else {
              this.loading = false;
              this.message = "Failed verify google captcha!";
              console.log("Failed verify google captcha!");
            }
          })
          .catch(error => {
            console.log(error.response.data);
          });
      } else {
        alert("Failed captcha");
      }
    },
    onExpired: function() {
      console.log("Expired");
    },

    onSuccess(response) {},

    clear() {}
  },

  mounted() {
    this.csrf = window.laravel.csrfToken;
    // set default value
    this.incidentDate = this.saveDateTime(
      this.incidentDatePicker,
      this.incidentTime + " " + this.incidentTimeAMPM
    );
    this.personalDOB = this.saveDate(this.personalDOBPicker);
  }
};
</script>

<style>
  #tab_container .container_item {
    display: none;
  }

  #tab_container .container_item.is-active {
    display: block;
  }

  .wizard-icon-circle i {
    font-style: normal;
  }

  .wizard-nav li .stepTitle {
    margin-top: 0.5em;
  }

  .wizard-nav li .stepTitle.active {
    font-size: 1.3em;
  }

  #datepicker,
  #datepicker2 {
    float: left;
    color: rgba(0, 0, 0, 0);
    border: none;
    box-shadow: none;
    width: 3em;
    background: url("/images/UI/calendar-icon.png");
    background-size: contain;
    background-repeat: no-repeat;
  }

  #incidentTime {
    width: 4em;
    margin-left: 2em;
    display: inline;
  }
</style>


// WEBPACK FOOTER //
// Modules/OnlineForm/Resources/assets/js/components/LayoutElectedMembersAttendance.vue